import React, { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

/**
 * Styled listbox — same appearance in every browser (unlike native &lt;select&gt;).
 * Drop-in friendly: onChange receives a synthetic event { target: { value } }.
 *
 * @param {{ value: string|number, label: React.ReactNode }[]} options
 */
export function SelectDropdown({
  value,
  onChange,
  options = [],
  disabled = false,
  className = '',
  placeholder = 'Select…',
  id,
  'aria-label': ariaLabel,
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  const strVal = value != null && value !== '' ? String(value) : ''
  const selected = options.find((o) => String(o.value) === strVal)
  const display = selected?.label ?? (strVal === '' ? placeholder : strVal)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <div className={`relative ${className}`.trim()} ref={rootRef}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`input-field flex w-full items-center justify-between gap-2 text-left text-sm text-gray-900 ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer bg-white'
        }`}
      >
        <span className="min-w-0 truncate">{display}</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && !disabled && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 z-[100] mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {options.map((opt, idx) => {
            const isSel = String(opt.value) === strVal
            return (
              <li key={`opt-${idx}-${String(opt.value)}`} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  className={`flex w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    isSel ? 'bg-psg-teal-50 font-medium text-psg-teal-900' : 'text-gray-900'
                  }`}
                  onClick={() => {
                    if (onChange) {
                      onChange({ target: { value: opt.value } })
                    }
                    setOpen(false)
                  }}
                >
                  {opt.label}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
